import { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getEligibleBusinessesThunk, getDealCategories } from "../../redux/slices/dealUploadSlice";
import { Card, Typography, Radio, Divider, Alert } from "antd";
import CustomSelect from "../../components/CustomSelect/CustomSelect"
import CustomButton from "../../components/CustomButton/Button";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./DealsUpload.scss";

const { Title, Text } = Typography;

const DealsUpload = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedBusiness, setSelectedBusiness] = useState(undefined);
  const [selectedDealType, setSelectedDealType] = useState(undefined);

  const {
    businessList,
    loading,
    error,
    dealCategories,
    dealCategoriesLoading
  } = useSelector((state) => state.dealUpload);

  useEffect(() => {
    dispatch(getEligibleBusinessesThunk());
    dispatch(getDealCategories());
  }, [dispatch]);

  const businessOptions = useMemo(() => {
    return (businessList || [])
      .filter((item) => {
        // Business status must be Active if statusId is present
        const busStatusVal = item.statusId || item.businessStatusId || item.orgStatusId;
        if (busStatusVal && busStatusVal !== "USM1000001") {
          return false;
        }
        const statusNameLower = (item.statusName || item.status || "").toLowerCase();
        if (statusNameLower && statusNameLower !== "active" && !statusNameLower.includes("active")) {
          return false;
        }

        // Filter enterprises to only those where Enterprise and Credit Limit statuses are Active
        const validEnterprises = (item.enterprises || []).filter((ent) => {
          const entStatusVal = ent.statusId || ent.entStatusId || ent.enterpriseStatusId;
          if (entStatusVal && entStatusVal !== "USM1000002") return false;
          const entStatusNameLower = (ent.statusName || ent.status || "").toLowerCase();
          if (entStatusNameLower && entStatusNameLower !== "active" && !entStatusNameLower.includes("active")) return false;

          const clStatusVal = ent.busEntStatusId || ent.creditLimitStatusId;
          if (clStatusVal && clStatusVal !== "USM1000002") return false;
          const clStatusNameLower = (ent.busEntStatusName || ent.creditLimitStatusName || "").toLowerCase();
          if (clStatusNameLower && clStatusNameLower !== "active" && !clStatusNameLower.includes("active")) return false;

          return true;
        });

        // If the business had enterprises configured, but none of them are active, hide the business
        if (item.enterprises && item.enterprises.length > 0 && validEnterprises.length === 0) {
          return false;
        }

        const categories = item.dealCategory || [];
        const hasEnterprise = validEnterprises.length > 0;

        const hasInvoiceStyle = categories.some(
          (cat) => cat.dealCategoryId === "DCT1000001" || cat.dealCategoryId === "DCT1000006"
        );

        // Any business with invoice style deals must have an active enterprise and credit limit
        if (hasInvoiceStyle && !hasEnterprise) {
          return false;
        }

        return true;
      })
      .map((item) => {
        const validEnterprises = (item.enterprises || []).filter((ent) => {
          const entStatusVal = ent.statusId || ent.entStatusId || ent.enterpriseStatusId;
          if (entStatusVal && entStatusVal !== "USM1000002") return false;
          const clStatusVal = ent.busEntStatusId || ent.creditLimitStatusId;
          if (clStatusVal && clStatusVal !== "USM1000002") return false;
          return true;
        });

        return {
          value: item.orgId,
          label: item.orgName,
          orgName: item.orgName,
          orgId: item.orgId,
          entId: item.entId,
          enterprises: validEnterprises,
          dealCategory: item.dealCategory || [],
          contactName: item.contactName,
          email: item.email,
          phoneNumber: item.phoneNumber,
        };
      });
  }, [businessList]);

  const selectedBusinessObj = businessOptions.find(
    (item) => String(item.value) === String(selectedBusiness)
  );

  const hasEnterprise =
    selectedBusinessObj?.enterprises?.length > 0;

  // Get the allowed dealCategoryId for the selected business
  // Get allowed deal categories for selected business
  const allowedDealCategoryIds =
    selectedBusinessObj?.dealCategory?.map((item) => item.dealCategoryId) || [];


  // Proceed button is enabled if a business is selected and a deal type is checked (selectedDealType is set)
  const isDisabled = !selectedBusiness || !selectedDealType || dealCategoriesLoading;

  const handleProceed = () => {
    // Find the selected deal category object for passing full details if needed
    const selectedDealCategory = (dealCategories || []).find(
      (cat) => cat.dealCategoryId === selectedDealType
    );
    const commonState = {
      businessId: selectedBusiness,
      businessName: selectedBusinessObj?.label,
      dealType: selectedDealType,
      dealCategory: selectedDealCategory,
    };

    // Route based on dealCategoryId (selectedDealType)
    if (!selectedDealCategory) {
      // fallback
      navigate("/deals-upload/", { state: commonState });
      return;
    }
    
    if (selectedDealCategory.dealCategoryId === "DCT1000001") { // Invoice Financing
      navigate("/invoice-upload", { state: commonState });
    } else if (selectedDealCategory.dealCategoryId === "DCT1000006") { // Supply Chain Financing
      navigate("/supply-chain-upload", { state: commonState });
    } else if (selectedDealCategory.dealCategoryId === "DCT1000002") { // Joint Ventures
      navigate("/joint-ventures", { state: commonState });
    } else if (selectedDealCategory.dealCategoryId === "DCT1000003") { // Startup Funding
      navigate("/startup-funding", { state: commonState });
    } else {
      navigate("/deals-upload/", { state: commonState });
    }
  };

  return (
    <div className="deals-upload-container">
      <Title level={2} className="page-title">
        Admin Deals Upload
      </Title>

      <Text className="page-subtitle">
        Upload deals for activated businesses
      </Text>

      <Card className="deals-card">

        <div className="field-block">
          <label className="field-label">
            Select Business <span>*</span>
          </label>

          <CustomSelect
            placeholder={loading ? "Loading..." : "-- Select Business --"}
            className="custom-ent-select"
            value={selectedBusiness}
            onChange={(value) => {
              setSelectedBusiness(value);
              setSelectedDealType(undefined);
            }}
            showSearch
            loading={loading}
            disabled={loading || !!error}
            options={businessOptions}   // ✅ FIXED HERE
          />

          {error && (
            <Text className="empty-text" type="danger">
              {error}
            </Text>
          )}
          {!loading && businessOptions.length === 0 && !error && (
            <Text className="empty-text">
              No activated businesses available
            </Text>
          )}
        </div>

        {/* Business Details Section */}
        {selectedBusiness && selectedBusinessObj && (
          <div className="business-details-card">
            <div className="business-details-title">Business Details</div>
            <div className="business-details-grid">
              <div>
                <div className="detail-row">
                  <span className="detail-label">Contact Person:</span>
                  <span className="detail-value">{selectedBusinessObj.contactName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedBusinessObj.email || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{selectedBusinessObj.phoneNumber || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="field-block">
          <label className="field-label">
            Select Deal Type <span>*</span>
          </label>

          <Radio.Group
            value={selectedDealType}
            onChange={(e) => setSelectedDealType(e.target.value)}
            className="deal-type-group"
          >
            {dealCategoriesLoading ? (
              <div>Loading deal types...</div>
            ) : (
              (dealCategories || []).map((cat) => {
                const isInvoiceStyle =
                  cat.dealCategoryId === "DCT1000001" || cat.dealCategoryId === "DCT1000006";

                const disabled =
                  !selectedBusiness ||
                  !allowedDealCategoryIds.includes(cat.dealCategoryId) ||
                  (isInvoiceStyle && !hasEnterprise);

                return (
                  <div
                    key={cat.dealCategoryId}
                    className={`deal-type-card ${selectedDealType === cat.dealCategoryId ? "selected" : ""
                      } ${disabled ? "disabled" : ""}`}
                  >
                    <Radio
                      value={cat.dealCategoryId}
                      disabled={disabled}
                    >
                      <div className="deal-content">
                        <div className="deal-title">
                          {cat.dealCategoryName}
                        </div>
                        <div className="deal-description">
                          {cat.dealCategoryDescription}
                        </div>
                      </div>
                    </Radio>
                  </div>
                );
              })
            )}
          </Radio.Group>
          <Divider />
        </div>

        <div className="proceed-action-row">
          <CustomButton
            type="primary"
            size="large"
            icon={<UploadOutlined />}
            disabled={isDisabled}
            onClick={handleProceed}
          >
            Proceed to Deal Upload
          </CustomButton>
        </div>
      </Card>

      <Alert
        className="note-box"
        type="info"
        showIcon={false}
        message={
          <span>
            <strong>Note:</strong> Deal uploads are only allowed for businesses
            whose selected deal type matches their registered deal type from the
            initial enquiry. If you need to upload a different deal type, please
            raise a deal type change request first.
          </span>
        }
      />
    </div>
  );
};

export default DealsUpload;




